import { useCallback, useMemo, useState } from 'react'
import { DndContext, DragOverlay, PointerSensor, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core'

import Box from '@mui/material/Box'
import { alpha } from '@mui/material/styles'

import { ActiveDragContext, DropZoneContext, useActiveDrag, useDropZone } from './Dnd.hooks'

const sensorOptions = { activationConstraint: { distance: 4 } }

const indicatorBaseSx = {
  position: 'absolute',
  inset: 0,
  zIndex: 5,
  pointerEvents: 'none',
  backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.1),
  backdropFilter: 'blur(8px) saturate(140%)',
  WebkitBackdropFilter: 'blur(8px) saturate(140%)',
  boxShadow: (theme) =>
    `inset 0 0 0 1.5px ${alpha(theme.palette.secondary.main, 0.45)}, inset 0 0 24px ${alpha(theme.palette.secondary.main, 0.2)}`,
  transition: 'opacity 150ms ease',
}

const dragSourceSx = ({ disabled, isDragging }) => ({
  touchAction: 'none',
  userSelect: 'none',
  cursor: disabled ? 'pointer' : 'grab',
  opacity: isDragging ? 0.4 : 1,
  '&:active': {
    zIndex: 10,
    cursor: disabled ? 'pointer' : 'grabbing',
  },
})

// Top-level provider. Manages the active drag, renders the DragOverlay clone
// via the consumer-supplied `renderOverlay`, and forwards drop events to
// `onDrop` (matches dnd-kit's onDragEnd signature).
export function DndBoard({ onDrop, renderOverlay, children }) {
  const [active, setActive] = useState(null)
  const sensors = useSensors(useSensor(PointerSensor, sensorOptions))

  const handleStart = useCallback(({ active: a }) => {
    const data = a.data.current
    setActive(data ? { item: data.item, fromZoneId: data.fromZoneId } : null)
  }, [])

  const handleEnd = useCallback(
    (event) => {
      setActive(null)
      onDrop?.(event)
    },
    [onDrop]
  )

  const handleCancel = useCallback(() => setActive(null), [])

  return (
    <ActiveDragContext.Provider value={active}>
      <DndContext sensors={sensors} onDragStart={handleStart} onDragEnd={handleEnd} onDragCancel={handleCancel}>
        {children}
        <DragOverlay dropAnimation={null}>{active ? renderOverlay?.(active.item) : null}</DragOverlay>
      </DndContext>
    </ActiveDragContext.Provider>
  )
}

// Wraps useDroppable. `canDrop(item)` decides whether the active drag is
// allowed here (used to disable the droppable). Exposes isOver/canDrop/isActive
// to descendant DropIndicators via context — consumers don't see dnd-kit.
export function DropZone({ id, canDrop, sx, children }) {
  const active = useActiveDrag()
  const allowed = active ? (canDrop ? canDrop(active.item) : true) : true

  const { isOver, setNodeRef } = useDroppable({ id, disabled: !allowed })

  const value = useMemo(
    () => ({
      isOver,
      canDrop: allowed,
      isActive: isOver && allowed && active?.fromZoneId !== id,
    }),
    [isOver, allowed, active, id]
  )

  return (
    <DropZoneContext.Provider value={value}>
      <Box ref={setNodeRef} sx={sx}>
        {children}
      </Box>
    </DropZoneContext.Provider>
  )
}

// Frosted drop-target indicator. Visibility driven by DropZone context.
// Position via `sx` (e.g. `inset: 4, top: 40` to clear a column title).
export function DropIndicator({ sx }) {
  const { isActive } = useDropZone()
  return <Box sx={[indicatorBaseSx, { opacity: isActive ? 1 : 0 }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]} />
}

// Wraps useDraggable. Encodes { item, fromZoneId } in drag data; consumers
// read `active.data.current.item` in their onDrop handler. The wrapper Box
// holds listeners/ref and applies drag opacity so the heavy child stays idle.
export function DragSource({ id, item, fromZoneId, disabled, sx, children }) {
  const data = useMemo(() => ({ item, fromZoneId }), [item, fromZoneId])
  const { isDragging, attributes, listeners, setNodeRef } = useDraggable({ id, data, disabled })

  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      sx={[dragSourceSx({ disabled, isDragging }), ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
    >
      {children}
    </Box>
  )
}
